import { useEffect, useMemo, useState } from 'react'
import { api, can, type Vendor } from '../lib/api'
import type { ExitSummary } from '../lib/exit'
import { personLabel, usePeopleNames } from '../lib/people'
import { DataTable, ErrorBox, type Column } from '../components/DataTable'
import { Dialog, MiniTrack, Status } from '../components/kit'
import { Avatar, Skeleton } from '../components/ui'
import { loadExitBoard } from '../components/exit/board'
import {
  TRIGGER_WORDS,
  dayMonth,
  exitStatus,
  nowWith,
  openFor,
  proposerWords,
  shortPerson,
  trackLabel,
  trackStates,
  truncate,
} from '../components/exit/words'
import '../styles/exit.css'

// Every exit in the tenant on one board: what is proposed and waiting for a
// decision, what is under way and where its sign-off has reached, and who has
// been signed out this year.
//
// # WHY A BOARD OF CARDS
//
// The screen this replaces was three tables of vendor names under paragraphs
// explaining each table. It answered "which vendors" and not "where is each
// one", which is the question somebody opening Offboarding has. A card carries
// the sign-off chain as a small track, who has the exit now and one status,
// so the whole estate's exits read at a glance. The table view stays for a
// long list, and the choice is remembered on this device.
//
// Deciding and signing happen in the exit's workspace, beside the chain and
// the evidence. A card takes the reader there.

type View = 'board' | 'table'
const VIEW_KEY = 'tprm.offboarding.view'

function readView(): View {
  try {
    return localStorage.getItem(VIEW_KEY) === 'table' ? 'table' : 'board'
  } catch {
    return 'board'
  }
}

function exitHref(vendorId: string): string {
  return '#/exits/' + vendorId
}

const LANES: { key: ExitSummary['laneState']; title: string; empty: string }[] = [
  { key: 'PROPOSED', title: 'Proposed', empty: 'Nothing is waiting for a decision' },
  // The lanes carry the vocabulary's words (lib/status.ts): an exit under way
  // is Offboarding, a finished one is Closed.
  { key: 'UNDER_WAY', title: 'Offboarding', empty: 'No vendor is offboarding' },
  { key: 'SIGNED_OUT', title: 'Closed', empty: 'None closed in the last year' },
]

export function Exits() {
  const [exits, setExits] = useState<ExitSummary[] | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [view, setView] = useState<View>(readView)
  const [proposing, setProposing] = useState(false)
  const names = usePeopleNames()
  const label = (s: string) => personLabel(names, s)

  useEffect(() => {
    let live = true
    loadExitBoard()
      .then((rows) => {
        if (live) setExits(rows)
      })
      .catch((err) => {
        if (live) setError(err)
      })
    return () => {
      live = false
    }
  }, [])

  function choose(next: View) {
    setView(next)
    try {
      localStorage.setItem(VIEW_KEY, next)
    } catch {
      /* the choice lasts this visit only */
    }
  }

  if (error != null) return <ErrorBox error={error} what="Offboarding" />
  if (!exits)
    return (
      <div className="p1-page exit-page">
        <h1>Offboarding</h1>
        <Skeleton lines={6} height={18} />
      </div>
    )

  const mayDecide = can('offboarding.run')
  const inLane = (k: ExitSummary['laneState']) => exits.filter((x) => x.laneState === k)
  const counts = LANES.map((l) => inLane(l.key).length)

  return (
    <div className="p1-page exit-page">
      <div className="exit-head">
        <div className="exit-head-grow">
          <h1>Offboarding</h1>
          <p className="exit-meta">
            {counts[0]} proposed, {counts[1]} offboarding, {counts[2]} closed
          </p>
        </div>
        <div className="exit-actions">
          <span className="exit-seg" role="group" aria-label="Show offboarding as">
            <button type="button" aria-pressed={view === 'board'} onClick={() => choose('board')}>
              Board
            </button>
            <button type="button" aria-pressed={view === 'table'} onClick={() => choose('table')}>
              Table
            </button>
          </span>
          {mayDecide && (
            <button type="button" className="primary" onClick={() => setProposing(true)}>
              Propose offboarding
            </button>
          )}
        </div>
      </div>

      {view === 'board' ? (
        <div className="exit-board">
          {LANES.map((lane, i) => {
            const rows = inLane(lane.key)
            return (
              <section key={lane.key} className={'exit-lane exit-lane-' + lane.key} aria-labelledby={'lane-' + lane.key}>
                <h2 id={'lane-' + lane.key}>
                  {lane.title} <span className="exit-lane-count">{counts[i]}</span>
                </h2>
                {rows.length === 0 ? (
                  <p className="exit-lane-empty">{lane.empty}</p>
                ) : (
                  rows.map((x) => <ExitCard key={x.vendorId} x={x} mayDecide={mayDecide} label={label} />)
                )}
              </section>
            )
          })}
        </div>
      ) : (
        <ExitTable exits={exits} mayDecide={mayDecide} label={label} />
      )}

      <ProposeDrawer open={proposing} onClose={() => setProposing(false)} leaving={exits} />
    </div>
  )
}

function reasonLine(x: ExitSummary): string {
  return x.reason?.trim() || TRIGGER_WORDS[x.triggerKind] || (x.laneState === 'SIGNED_OUT' ? 'Closed' : 'No reason recorded')
}

// Who has the exit now and since when, as two short lines beside an avatar:
// the office whose stage it has reached, whoever proposed it, or whoever
// signed it out. Two lines rather than one sentence, so neither is cut off at
// the width a card has in a lane.
function whoNow(x: ExitSummary, label: (s: string) => string): { name: string; sub: string } {
  switch (x.laneState) {
    case 'PROPOSED':
      return { name: shortPerson(proposerWords(x.proposedBy, label)), sub: 'Proposed' + (x.proposedAt ? ' ' + dayMonth(x.proposedAt) : '') }
    case 'SIGNED_OUT':
      return { name: x.closedBy ? shortPerson(label(x.closedBy)) : 'Closed', sub: x.closedAt ? 'Closed ' + dayMonth(x.closedAt) : '' }
  }
  if (!x.currentJob) return { name: 'Every stage signed', sub: openFor(x.daysOpen, x.plannedDays) }
  return { name: x.currentJob, sub: x.dueOn ? 'Due ' + dayMonth(x.dueOn) : openFor(x.daysOpen, x.plannedDays) }
}

// Where the sign-off has reached, in the few words beside the track.
function trackCount(x: ExitSummary): string {
  if (x.laneState === 'PROPOSED') return x.stageCount + ' to sign'
  if (x.stageIndex >= x.stageCount) return 'All signed'
  return x.stageIndex + ' of ' + x.stageCount + ' signed'
}

function ExitCard({ x, mayDecide, label }: { x: ExitSummary; mayDecide: boolean; label: (s: string) => string }) {
  const status = exitStatus(x, mayDecide)
  const reason = reasonLine(x)
  const who = whoNow(x, label)
  return (
    <a
      className="exit-card"
      href={exitHref(x.vendorId)}
      aria-label={x.vendorName + ': ' + (x.laneState === 'UNDER_WAY' ? trackLabel(x) : nowWith(x, label)) + ', ' + status.text}
    >
      <span className="exit-card-top">
        <b title={x.vendorName}>{x.vendorName}</b>
        <span className="exit-code">{x.vendorCode}</span>
      </span>
      <span className="exit-card-reason" title={reason}>
        {truncate(reason, 80)}
      </span>
      {x.stageCount > 0 ? (
        <span className="exit-card-track">
          <MiniTrack states={trackStates(x)} label={trackLabel(x)} />
          <span className="exit-card-count">{trackCount(x)}</span>
        </span>
      ) : (
        <span className="exit-card-note">No sign-off on record</span>
      )}
      <span className="exit-card-foot">
        <span className="exit-card-who">
          <Avatar name={who.name} size={28} />
          <span className="exit-card-who-text">
            <b>{who.name}</b>
            {who.sub && <small>{who.sub}</small>}
          </span>
        </span>
        <Status tone={status.tone} title={x.blocker}>
          {status.text}
        </Status>
      </span>
    </a>
  )
}

function ExitTable({ exits, mayDecide, label }: { exits: ExitSummary[]; mayDecide: boolean; label: (s: string) => string }) {
  const columns: Column<ExitSummary>[] = [
    {
      key: 'vendor',
      header: 'Vendor',
      sortValue: (x) => x.vendorName.toLowerCase(),
      render: (x) => (
        <span>
          <a href={exitHref(x.vendorId)}>
            <strong>{x.vendorName}</strong>
          </a>
          <div className="cell-sub mono">{x.vendorCode}</div>
        </span>
      ),
    },
    {
      key: 'reason',
      header: 'Reason',
      render: (x) => {
        const r = reasonLine(x)
        return <span title={r}>{truncate(r, 48)}</span>
      },
    },
    {
      key: 'signoff',
      header: 'Sign-off',
      sortValue: (x) => (x.stageCount ? x.stageIndex / x.stageCount : -1),
      render: (x) =>
        x.stageCount > 0 ? (
          <span className="exit-table-track">
            <MiniTrack states={trackStates(x)} label={trackLabel(x)} />
            <span className="muted">
              {Math.min(x.stageIndex, x.stageCount)} of {x.stageCount}
            </span>
          </span>
        ) : (
          <span className="muted">Not recorded</span>
        ),
    },
    { key: 'now', header: 'Now with', render: (x) => nowWith(x, label) },
    {
      key: 'open',
      header: 'Open for',
      sortValue: (x) => x.daysOpen ?? null,
      render: (x) => (x.daysOpen === undefined ? <span className="muted">Not recorded</span> : x.daysOpen + (x.daysOpen === 1 ? ' day' : ' days')),
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (x) => exitStatus(x, mayDecide).text,
      render: (x) => {
        const s = exitStatus(x, mayDecide)
        return (
          <Status tone={s.tone} title={x.blocker}>
            {s.text}
          </Status>
        )
      },
    },
  ]
  return (
    <section className="panel">
      <DataTable
        caption="Offboarding"
        columns={columns}
        rows={exits}
        rowKey={(x) => x.vendorId}
        empty={{ title: 'No offboarding is proposed, in progress or closed this year.', detail: 'Propose one from here or from a vendor.' }}
      />
    </section>
  )
}

// Choosing the vendor to propose an exit for. The proposal itself is made in
// that vendor's exit workspace, where the chain it would be held to is in
// front of the person proposing it.
function ProposeDrawer({ open, onClose, leaving }: { open: boolean; onClose: () => void; leaving: ExitSummary[] }) {
  const [q, setQ] = useState('')
  const [vendors, setVendors] = useState<Vendor[] | null>(null)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    if (!open || vendors !== null) return
    let live = true
    api
      .vendors({ status: ['ACTIVE', 'SUSPENDED'] })
      .then((r) => {
        if (live) setVendors(r.vendors ?? [])
      })
      .catch((err) => {
        if (live) setError(err)
      })
    return () => {
      live = false
    }
  }, [open, vendors])

  const busy = useMemo(() => new Set(leaving.map((x) => x.vendorId)), [leaving])
  const needle = q.trim().toLowerCase()
  const shown = (vendors ?? [])
    .filter((v) => !busy.has(v.id))
    .filter((v) => !needle || (v.displayName + ' ' + v.legalName + ' ' + v.vendorCode).toLowerCase().includes(needle))
    .slice(0, 50)

  return (
    <Dialog shape="drawer" kind="form" open={open} onClose={onClose} title="Propose offboarding">
      <div className="field">
        <label htmlFor="propose-search">Vendor</label>
        <input
          id="propose-search"
          type="search"
          value={q}
          placeholder="Name or code"
          onChange={(e) => setQ(e.target.value)}
        />
        <p className="field-note">Active and suspended vendors not already offboarding</p>
      </div>
      {error != null && <ErrorBox error={error} what="The vendors" />}
      {vendors === null && error == null && <Skeleton lines={4} height={16} />}
      {vendors !== null && (
        <ul className="exit-pick">
          {shown.length === 0 && <li className="muted">No vendor matches</li>}
          {shown.map((v) => (
            <li key={v.id}>
              <a href={exitHref(v.id)} onClick={onClose}>
                <b>{v.displayName?.trim() || v.legalName}</b>
                <span className="exit-code">{v.vendorCode}</span>
                {v.status === 'SUSPENDED' && <Status tone="warn">Suspended</Status>}
              </a>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  )
}
